// ─── useHeaderNav ─────────────────────────────────────────────────────────────
// The app's logged-in navigation, as data + one navigate function.
//
// Lifted verbatim out of AppHeader so the mobile drawer (MobileNavBar) can
// drive the SAME menu rather than carrying a second copy of the tab list,
// the localStorage keys and the deep-link rules. Those keys are a contract
// with DashboardPage — it reads `wq_activeTab` and `wq_current*Index` on
// mount to decide which tab and sub-page to open — so a divergent second
// copy would send mobile users to the wrong screen the moment either side
// changed.
//
// Returns:
//   tabs            — the six top-level destinations, in order
//   hasDropdown(t)  — whether a tab has sub-items
//   dropdownConfig  — { [tab]: { items, indexKey } }
//   goToDashboard() — persist tab + sub-index, then navigate
//   projectId       — resolved project to deep-link into (may be null briefly)

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { projectsApi } from './projectsApi'
import { dashboardApi } from './dashboardApi'
import { listReports } from './reportApi'

export const NAV_TABS = ['Dashboard', 'Data Analytics', 'Report', 'Gallery', 'Settings', 'Live']
export const NAV_TABS_WITH_DROPDOWN = new Set(['Dashboard', 'Data Analytics', 'Report', 'Settings', 'Live'])

const DATA_ANALYTICS_ITEMS = ['Visualize', 'Validate', 'Formulas']
// Keep this list in sync with DashboardPage.jsx `settingsMenuItems` — the
// dropdown writes its index into `wq_currentSettingsIndex`, which
// DashboardPage reads on mount to open the right sub-page.
const SETTINGS_ITEMS       = ['Users', 'Project', 'API', 'Streaming Method',
                              'Public Portal', 'Embedded Data Applet', 'RSS Feed']
const LIVE_ITEMS           = ['Data Logger', 'Communications', 'Sensor Readings']

// localStorage key for the most-recently-active project id, written by the
// header itself so a return visit to /account can deep-link the menus back
// into the same project the user was last looking at.
const LAST_PROJECT_KEY = 'wq_last_project_id'

// `enabled` guards the two network effects. MobileNavBar mounts on the public
// marketing homepage as well, where an anonymous visitor has no projects and
// the authed /projects call would just 401 on every page load — so it passes
// enabled=false until the user is signed in.
export function useHeaderNav({ enabled = true } = {}) {
  const navigate = useNavigate()
  const { projectId: urlProjectId } = useParams()

  const [projectId, setProjectId] = useState(() => {
    try { return urlProjectId || localStorage.getItem(LAST_PROJECT_KEY) || null }
    catch { return urlProjectId || null }
  })
  const [templates, setTemplates] = useState([])
  const [reports, setReports] = useState([])

  // Persist the project id from the URL whenever it changes so /account
  // (and any other non-dashboard page) can deep-link back to it later.
  useEffect(() => {
    if (urlProjectId) {
      try { localStorage.setItem(LAST_PROJECT_KEY, urlProjectId) } catch { /* private mode */ }
      setProjectId(urlProjectId)
    }
  }, [urlProjectId])

  // Resolve a project id if we don't have one yet — fetch the user's first
  // project so menu clicks have somewhere to navigate to.
  useEffect(() => {
    if (!enabled || projectId) return
    let cancelled = false
    projectsApi.fetchProjects(1, 1)
      .then((res) => {
        if (cancelled) return
        const first = (res?.success && Array.isArray(res.data) && res.data[0]) || null
        if (first?.id) {
          setProjectId(String(first.id))
          try { localStorage.setItem(LAST_PROJECT_KEY, String(first.id)) } catch { /* private mode */ }
        }
      })
      .catch(() => { /* swallow — menus just won't deep-link */ })
    return () => { cancelled = true }
  }, [enabled, projectId])

  // Pull the Dashboard menu's items (templates) for the chosen project.
  useEffect(() => {
    if (!enabled || !projectId) { setTemplates([]); return }
    let cancelled = false
    dashboardApi.getProjectTemplates(projectId)
      .then((res) => {
        if (cancelled) return
        // The endpoint returns {templates: [...]} — `.results` and a bare
        // array are only legacy fallbacks. Reading `.results` first meant
        // `res.data` (an object, not an array) fell through the Array.isArray
        // guard and the menu collapsed to just "Config Dashboards". Same
        // extraction + is_active filter DashboardPage uses, so the header and
        // the page always list the same dashboards.
        const list = res?.data?.templates || res?.data?.results || res?.data || []
        const arr  = Array.isArray(list) ? list : []
        setTemplates(arr.filter((t) => t.is_active !== false))
      })
      .catch(() => { if (!cancelled) setTemplates([]) })
    return () => { cancelled = true }
  }, [enabled, projectId])

  // Pull the project's saved reports so the Report menu matches the desktop
  // dropdown (which lists saved reports + View All / Create) instead of a
  // static placeholder list. Same source (reportApi.listReports) and shape
  // (res.reports) DashboardPage uses, so header and page never disagree.
  useEffect(() => {
    if (!enabled || !projectId) { setReports([]); return }
    let cancelled = false
    listReports(projectId)
      .then((r) => { if (!cancelled) setReports(r?.reports || []) })
      .catch(() => { if (!cancelled) setReports([]) })
    return () => { cancelled = true }
  }, [enabled, projectId])

  // "Config Dashboards" is ALWAYS listed now (like the Settings menu). Opening
  // it is view-only; the modal's edit controls stay disabled until the user
  // turns on Edit Mode (see ConfigDashboardsModal's EditModeGate).
  const dashboardItems = [
    ...templates.map((t) => t.name).filter(Boolean),
    'Config Dashboards',
  ]

  // Report menu mirrors the desktop dropdown: saved reports first, then the two
  // actions. Items are objects { label, report } so the drawer can open the SAME
  // report view / list / create (not a static placeholder) — DashboardPage's
  // wq_nav_change handler reads the `report` payload. Plain-string items from the
  // other tabs keep working because the consumers read `item.label ?? item`.
  const reportItems = [
    ...reports.map((r) => ({ label: r.name, report: { action: 'view', id: r.id } })),
    { label: 'View All Reports', report: { action: 'list' } },
    { label: '+ Create Report',  report: { action: 'create' } },
  ]

  // Items per tab + their target indexKey in localStorage.
  const dropdownConfig = {
    'Dashboard':      { items: dashboardItems,       indexKey: 'wq_currentMenuIndex' },
    'Data Analytics': { items: DATA_ANALYTICS_ITEMS, indexKey: 'wq_currentDataAnalyticsIndex' },
    'Report':         { items: reportItems,          indexKey: 'wq_currentReportIndex' },
    'Settings':       { items: SETTINGS_ITEMS,       indexKey: 'wq_currentSettingsIndex' },
    'Live':           { items: LIVE_ITEMS,           indexKey: 'wq_currentLiveIndex' },
  }

  // Go to /dashboard/project/<id> (or just /dashboard if we don't have an
  // id yet), after persisting the tab + sub-index DashboardPage reads on
  // mount so the user lands in the right place.
  const goToDashboard = ({ tab, indexKey, indexValue } = {}) => {
    // The clicked item's label travels with the event so the page can spot
    // special entries ("Config Dashboards") without needing its own copy of
    // the item list.
    // Items are usually plain strings, but the Report tab carries objects
    // { label, report } — unwrap both so the label travels for special-entry
    // detection and the report action travels for DashboardPage to act on.
    const item = (indexValue != null && tab && dropdownConfig[tab])
      ? dropdownConfig[tab].items[indexValue]
      : null
    const label  = item == null ? null : (typeof item === 'string' ? item : item.label)
    const report = (item && typeof item === 'object') ? item.report : undefined
    try {
      if (tab) localStorage.setItem('wq_activeTab', tab)
      if (indexKey != null && indexValue != null) {
        localStorage.setItem(indexKey, String(indexValue))
      }
      // Persist the report action too, so a FRESH DashboardPage mount (e.g.
      // clicking a report in AppHeader from a non-dashboard page) restores the
      // same report — the in-place wq_nav_change event is only received when the
      // page is already mounted (the mobile-drawer case). Mirrors the keys
      // DashboardPage reads on mount; only 'view' is restored, matching its
      // rule of not reopening a half-finished create/edit editor.
      if (report && typeof report === 'object') {
        if (report.action === 'view' && report.id != null) {
          localStorage.setItem('wq_reportMode', 'view')
          localStorage.setItem('wq_reportId', String(report.id))
        } else {
          localStorage.removeItem('wq_reportMode')
          localStorage.removeItem('wq_reportId')
        }
      }
    } catch { /* private mode */ }

    // DashboardPage reads these keys ONLY in its useState initialisers, i.e.
    // once at mount. When the user is already on /dashboard/project/<id> —
    // which is always the case for the mobile drawer — navigate() to the same
    // path is a no-op, nothing remounts, and the click appears to do nothing.
    // This event lets the mounted page apply the change in place. Harmless
    // elsewhere: nobody else listens, and a genuine route change still works
    // through the navigate() below.
    try {
      window.dispatchEvent(new CustomEvent('wq_nav_change', {
        detail: { tab, indexKey, indexValue, label, report },
      }))
    } catch { /* non-browser / old engine */ }

    navigate(projectId ? `/dashboard/project/${projectId}` : '/dashboard')
  }

  return {
    tabs: NAV_TABS,
    hasDropdown: (tab) => NAV_TABS_WITH_DROPDOWN.has(tab),
    dropdownConfig,
    goToDashboard,
    projectId,
  }
}
